import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, Printer, TrendingDown, TrendingUp, Minus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';
import AndamentoQualita from '@/components/AndamentoQualita';

const ANNI = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2];
const it = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const val = (i) => i.valore === null || i.valore === undefined ? 'n.d.' : `${i.valore}${i.unita ? ` ${i.unita}` : ''}`;

const Freccia = ({ i }) => {
  if (i.variazione_pct === null || i.variazione_pct === undefined) return <Minus className="w-3.5 h-3.5 text-slate-300" />;
  const Icon = i.variazione_pct > 0 ? TrendingUp : TrendingDown;
  const colore = i.in_miglioramento === null ? 'text-slate-400' : i.in_miglioramento ? 'text-emerald-600' : 'text-red-600';
  return <span className={`flex items-center gap-1 text-[11px] font-medium ${colore}`}><Icon className="w-3.5 h-3.5" />{Math.abs(i.variazione_pct)}%</span>;
};

export default function CruscottoQualita() {
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [trimestre, setTrimestre] = useState(String(Math.floor(new Date().getMonth() / 3) + 1));
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const carica = useCallback(() => {
    setLoading(true);
    api.get('/qualita/cruscotto', { params: { anno, trimestre } })
      .then(r => setD(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Errore nel caricamento'))
      .finally(() => setLoading(false));
  }, [anno, trimestre]);

  useEffect(() => { carica(); }, [carica]);

  const stampa = () => {
    if (!d) return;
    const righe = d.indicatori.map(i => `<tr>
      <td style="font-size:11px">${i.nome}${i.nota ? ` <i>(${i.nota})</i>` : ''}</td>
      <td align="center" style="font-size:11px"><b>${val(i)}</b></td>
      <td align="center" style="font-size:11px">${i.precedente === null || i.precedente === undefined ? 'n.d.' : i.precedente}</td>
      <td align="center" style="font-size:11px">${i.variazione_pct === null ? '-' : `${i.variazione_pct > 0 ? '+' : ''}${i.variazione_pct}%`}</td>
    </tr>`).join('');
    const det = d.dettaglio;
    openPrintWindow(`Cruscotto qualita ${d.trimestre}T ${d.anno}`, `
      <h2>Cruscotto della qualita assistenziale</h2>
      <p><b>Periodo:</b> ${d.trimestre}&deg; trimestre ${d.anno} (dal ${it(d.periodo.da)} al ${it(d.periodo.a)}) &nbsp;&nbsp;
      <b>Confronto:</b> ${d.periodo_precedente.trimestre}&deg; trimestre ${d.periodo_precedente.anno} &nbsp;&nbsp;
      <b>Ospiti in carico:</b> ${d.ospiti_in_carico}</p>
      <table style="width:100%;border-collapse:collapse" border="1" cellpadding="5">
        <thead><tr><th align="left" style="font-size:11px">Indicatore</th><th style="font-size:11px">Valore</th>
        <th style="font-size:11px">Trim. prec.</th><th style="font-size:11px">Var.</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
      <h3 style="margin:16px 0 4px;font-size:13px">Dettaglio</h3>
      <p style="font-size:11px">Cadute: ${det.cadute.totale} (di cui ${det.cadute.gravi} con esito grave, ${det.cadute.notturne} nel turno notte).
      Luoghi: ${det.cadute.per_luogo.map(x => `${x.luogo} ${x.n}`).join(', ') || 'nessuno'}.</p>
      <p style="font-size:11px">Lesioni da pressione: ${det.lesioni.lesioni_nel_periodo} monitorate, ${det.lesioni.nuove_in_struttura} insorte in struttura,
      ${det.lesioni.guarite} guarite, ${det.lesioni.aperte_a_fine_periodo} ancora aperte.</p>
      <p style="font-size:11px">Contenzioni attive: ${det.contenzioni.contenzioni_attive} su ${det.contenzioni.ospiti_totali} ospiti
      (${det.contenzioni.conformi} conformi, ${det.contenzioni.senza_prescrizione} senza prescrizione, ${det.contenzioni.senza_consenso} senza consenso,
      ${det.contenzioni.da_rivalutare} da rivalutare).</p>
      <p style="font-size:11px">Scale di valutazione: ${det.scale.valutazioni_presenti} su ${det.scale.valutazioni_attese} attese,
      ${det.scale.ospiti_completi} ospiti con quadro completo, ${det.scale.esiti_a_rischio} esiti a rischio elevato.</p>
      <p style="font-size:11px">Somministrazioni: ${det.somministrazioni.totali} registrate, ${det.somministrazioni.mancate} mancate o rifiutate.</p>
      <p style="font-size:11px">Eventi avversi per tipo: ${det.eventi_avversi.map(e => `${e.tipo} ${e.n}`).join(', ') || 'nessuno'}.</p>
      <h3 style="margin:16px 0 4px;font-size:13px">Criticita e azioni</h3>
      <ol style="font-size:11px;padding-left:18px">${d.criticita.map(c => `<li style="margin-bottom:3px">${c}</li>`).join('')}</ol>
      <p style="font-size:11px;margin-top:20px">Documento generato il ${it(d.generato_il)} da ${d.generato_da}.</p>
      <table style="width:100%;margin-top:30px;font-size:11px"><tr>
        <td>Il Coordinatore<br/><br/>______________________________</td>
        <td>Il Direttore Sanitario<br/><br/>______________________________</td>
      </tr></table>
    `);
  };

  if (loading && !d) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;

  return (
    <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="cruscotto-qualita-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Cruscotto della Qualita</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Indicatori del {d?.trimestre}° trimestre {d?.anno} · confronto con il {d?.periodo_precedente.trimestre}° trimestre {d?.periodo_precedente.anno} · {d?.ospiti_in_carico} ospiti in carico
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={anno} onValueChange={setAnno}>
            <SelectTrigger className="w-28 h-9 text-xs" data-testid="cruscotto-anno"><SelectValue /></SelectTrigger>
            <SelectContent>{ANNI.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={trimestre} onValueChange={setTrimestre}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="cruscotto-trimestre"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4].map(t => <SelectItem key={t} value={String(t)}>{t}° trimestre</SelectItem>)}</SelectContent>
          </Select>
          <Button className="gap-2 bg-brand hover:bg-brand-dark text-white" onClick={stampa} data-testid="stampa-cruscotto-btn">
            <Printer className="w-4 h-4" />Stampa per ASL
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cruscotto-indicatori">
        {(d?.indicatori || []).map((i, idx) => (
          <Card key={idx} data-testid={`indicatore-${idx}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider leading-tight">{i.nome}</p>
                <Freccia i={i} />
              </div>
              <p className="text-3xl font-bold text-slate-900 font-[Cinzel] mt-1">{val(i)}</p>
              <p className="text-[11px] text-slate-400">
                {i.precedente === null || i.precedente === undefined ? (i.nota || 'nessun confronto disponibile') : `trimestre precedente: ${i.precedente}${i.unita ? ` ${i.unita}` : ''}`}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className={d?.criticita?.length && !d.criticita[0].startsWith('Nessuna') ? 'border-amber-200 bg-amber-50/40' : ''}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            {d?.criticita?.[0]?.startsWith('Nessuna')
              ? <ShieldCheck className="w-4 h-4 text-emerald-600" />
              : <AlertTriangle className="w-4 h-4 text-amber-600" />}
            Criticita e azioni da intraprendere
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1.5" data-testid="cruscotto-criticita">
            {(d?.criticita || []).map((c, i) => (
              <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-brand">•</span>{c}</li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <AndamentoQualita />

      {d?.dettaglio?.eventi_avversi?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-slate-800">Eventi avversi del trimestre</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {d.dettaglio.eventi_avversi.map(e => (
              <Badge key={e.tipo} variant="outline" className="text-[11px] capitalize">{String(e.tipo).replace(/_/g, ' ')} · {e.n}</Badge>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
